import clsx from 'clsx';
import { memo, type ReactElement } from 'react';

import type { SharedIconProps } from '../../types/SharedIconProps';
import type { SharedLinkProps } from '../../types/SharedLinkProps';
import type { TextVariant } from '../../types/TextVariant';
import type { TextTone } from '../Text/types';

import { Icon } from '../Icon/Icon';
import { Layout } from '../Layout/Layout';
import { Link } from '../Link/Link';
import { Text } from '../Text/Text';
import { Tooltip } from '../Tooltip/Tooltip';
import './TextBlock.css';

export type TextBlockStroke = 'default' | 'strong';

export type TextBlockLinkProps = Pick<SharedLinkProps, 'href' | 'external'>;

export type TextBlockProps = {
  text: string;
  icon?: SharedIconProps;
  link?: TextBlockLinkProps;
  secondaryText?: string;
  secondaryTone?: TextTone;
  secondaryTooltip?: string;
  stroke?: TextBlockStroke;
  tooltip?: string;
  /** Overrides the primary text's variant instead of deriving it from `stroke`. */
  variant?: TextVariant;
};

function wrapTooltip(content: ReactElement, tooltip?: string) {
  if (!tooltip) {
    return content;
  }

  return (
    <Tooltip tooltip={tooltip} __private={{ wrapTrigger: true }}>
      {content}
    </Tooltip>
  );
}

export const TextBlock = memo(
  ({
    text,
    icon,
    link,
    secondaryText,
    secondaryTone = 'subdued',
    secondaryTooltip,
    stroke = 'default',
    tooltip,
    variant,
  }: TextBlockProps) => {
    const href = link?.href;
    const external = link?.external ?? false;
    const primaryVariant =
      variant ?? (stroke === 'strong' ? 'body-semi-m' : 'body-m');
    const linkVariant = stroke === 'strong' ? 'default' : 'subdued';
    const isInline = !icon && !secondaryText;
    const primaryAs = isInline ? 'span' : 'p';

    return (
      <Layout
        align={isInline ? 'center' : 'start'}
        className={clsx('eds-text-block', isInline && 'eds-text-block--inline')}
        spacing="xs"
      >
        {icon && (
          <Layout
            align="center"
            className="eds-text-block--icon"
            spacing="none"
          >
            <Icon
              filled={icon.filled}
              icon={icon.key}
              label={icon.label}
              size={icon.size ?? 's'}
            />
          </Layout>
        )}
        <Layout
          className="eds-text-block--content"
          direction="column"
          flex="1"
          spacing="none"
        >
          {wrapTooltip(
            href ? (
              <Link
                external={external}
                href={href}
                size="m"
                text={text}
                variant={linkVariant}
              />
            ) : (
              <Text as={primaryAs} variant={primaryVariant}>
                {text}
              </Text>
            ),
            tooltip,
          )}
          {secondaryText &&
            wrapTooltip(
              <Text as="p" tone={secondaryTone} variant="body-m">
                {secondaryText}
              </Text>,
              secondaryTooltip,
            )}
        </Layout>
      </Layout>
    );
  },
);

TextBlock.displayName = 'TextBlock';
